/**
 * Watch and steer one harness run (Jimi's, or an agent step's): whether it's running and for how
 * long, its conversation, the sandbox command running now, and Stop / Resume / a message it reads
 * at its next step, while those make sense. `base` is where datahub proxies the Harness Service's
 * control API for that kind of run: /agent-platform/jimi/runs for Jimi's (staff), an agent's
 * /agent-teams/{id}/runs for its steps'. Polled while it runs; close enough to live.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import ApiService from '@/services/api.service';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { controlErrorText as errorText, formatDateTime, isLive, useRunSummary, type RunSummary } from './data';

type Part =
  | { type: 'text' | 'reasoning'; text: string }
  | { type: 'dynamic-tool'; toolName: string; state: string; errorText?: string }
  | { type: string; data?: unknown };
type Message = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  display: 'visible' | 'hidden' | 'diagnostic';
  signal?: { tagName?: string; attributes?: Record<string, string> };
  timestamp?: string;
  parts: Part[];
};
type Snapshot = { messages: Message[] };
type Terminal = { command: string; output: string; running: boolean; exitCode?: number; at: string };

const api = () => ApiService.getInstance();

/** 42s, 3m 12s, 1h 4m. */
function duration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

/** The time now, ticking every second while `on`. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

/** A dot for a run's state: pulsing while it runs, ✓ finished, ✗ failed or stopped. */
export function RunDot({ base, runId }: { base: string; runId: string }) {
  const s = useRunSummary(base, runId).data;
  if (!s) return null;
  if (isLive(s)) return <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" aria-label="running" />;
  return s.status === 'completed' ? (
    <span className="text-xs text-green-600" aria-label="finished">
      ✓
    </span>
  ) : (
    <span className="text-xs text-destructive" aria-label={s.status}>
      ✗
    </span>
  );
}

function RunStatus({ s }: { s: RunSummary }) {
  const live = isLive(s);
  const now = useNow(live);
  const since = s.statusAt ? new Date(s.statusAt).getTime() : null;
  const took = since && s.startedAt ? duration(since - new Date(s.startedAt).getTime()) : null;
  const cost = s.costUsd ? ` · $${s.costUsd.toFixed(2)}` : '';
  if (s.status === 'not started')
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Starting…
      </p>
    );
  if (live)
    return (
      <p className="flex items-center gap-2 text-sm">
        <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
        Running{since ? ` for ${duration(now - since)}` : ''}
        {cost}
      </p>
    );
  if (s.status === 'completed')
    return (
      <p className="text-sm text-green-700">
        ✓ Finished{took ? ` in ${took}` : ''}
        {cost}
      </p>
    );
  return (
    <p className="text-sm text-destructive">
      ✗ {s.status === 'aborted' ? 'Stopped' : 'Failed'}
      {took ? ` after ${took}` : ''}
      {cost}
      {s.error ? `: ${s.error}` : ''}
    </p>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
    </div>
  );
}

/** One run, live: its status, controls, the sandbox's current command, the conversation, and steering. */
export function RunLive({
  base,
  runId,
  speaker,
  steerPlaceholder,
  resume,
  startOver,
  onChanged,
}: {
  base: string; // datahub's runs path for this kind of run
  runId: string;
  speaker: string; // who the run's own messages are from
  steerPlaceholder: string;
  resume: { path: string; body?: object }; // how this kind of run resumes
  startOver?: boolean; // only Jimi's triage runs can start fresh
  onChanged?: () => void;
}) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const status = useRunSummary(base, runId);
  const summary = status.data;
  // No summary (an older Harness Service): offer everything, as before.
  const live = !summary || isLive(summary);
  const settledBadly = !summary || summary.status === 'failed' || summary.status === 'aborted';
  const run = useQuery<Snapshot>({
    queryKey: ['agent-run', runId],
    queryFn: async () => (await api().request('GET', `${base}/${encodeURIComponent(runId)}`, { limit: 60 })) as Snapshot,
    // Close enough to live: the snapshot carries the sandbox's current command.
    refetchInterval: live ? 3_000 : false,
  });

  const act = useMutation({
    mutationFn: async ({ path, body }: { path: string; body?: object }) =>
      api().request('POST', `${base}/${encodeURIComponent(runId)}/${path}`, body ?? {}),
    onSuccess: (_data, vars) => {
      if (vars.path === 'messages') setMessage('');
      queryClient.invalidateQueries({ queryKey: ['agent-run', runId] });
      onChanged?.();
    },
  });

  const messages = (run.data?.messages ?? []).filter((m) => m.display !== 'hidden');
  const terminal = latestTerminal(run.data?.messages ?? []);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {summary && <RunStatus s={summary} />}
        {status.isError && (
          <p className="text-sm text-muted-foreground">Can't tell whether this run is still going (the Harness Service didn't say).</p>
        )}
        <div className="ml-auto flex gap-2">
          {settledBadly && (
            <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => act.mutate(resume)}>
              Resume
            </Button>
          )}
          {startOver && settledBadly && (
            <Button
              size="sm"
              variant="outline"
              disabled={act.isPending}
              onClick={() => act.mutate({ path: 'restart', body: { mode: 'fresh' } })}
            >
              Start over
            </Button>
          )}
          {live && (
            <Button size="sm" variant="destructive" disabled={act.isPending} onClick={() => act.mutate({ path: 'abort' })}>
              Stop
            </Button>
          )}
        </div>
      </div>
      {act.error && <p className="text-sm text-destructive">{errorText(act.error, 'That didn’t work.')}</p>}

      {terminal && (
        <div className="rounded-md border bg-muted/40 p-2">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span className="truncate font-mono">$ {terminal.command}</span>
            <span>{terminal.running ? 'running' : `exited ${terminal.exitCode ?? ''}`}</span>
          </div>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs">{terminal.output || ' '}</pre>
        </div>
      )}

      <div className="flex-1 space-y-3 overflow-y-auto pr-1">
        {run.isLoading ? (
          <Spinner />
        ) : run.error ? (
          <p className="text-sm text-destructive">{errorText(run.error, 'Could not load this run.')}</p>
        ) : (
          messages.map((m) => <MessageView key={m.id} message={m} speaker={speaker} />)
        )}
      </div>

      {live && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (message.trim()) act.mutate({ path: 'messages', body: { body: message } });
          }}
        >
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={steerPlaceholder} maxLength={2000} rows={3} />
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={act.isPending || !message.trim()}>
              Send
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** RunLive in a side sheet. */
export function RunSheet({
  runId,
  onClose,
  ...live
}: { runId: string | null; onClose: () => void } & Omit<Parameters<typeof RunLive>[0], 'runId'>) {
  return (
    <Sheet open={!!runId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-4 sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Run</SheetTitle>
          <SheetDescription className="break-all font-mono text-xs">{runId}</SheetDescription>
        </SheetHeader>
        {runId && <RunLive key={runId} runId={runId} {...live} />}
      </SheetContent>
    </Sheet>
  );
}

function MessageView({ message, speaker }: { message: Message; speaker: string }) {
  const who =
    message.role === 'assistant'
      ? speaker
      : message.role === 'system'
        ? message.signal?.attributes?.from
          ? `${message.signal.attributes.from} (${message.signal.tagName ?? 'signal'})`
          : (message.signal?.tagName ?? 'System')
        : 'Brief';
  return (
    <div className={`rounded-md border p-2 text-sm ${message.display === 'diagnostic' ? 'opacity-70' : ''}`}>
      <div className="mb-1 flex justify-between text-xs text-muted-foreground">
        <span className="font-medium">{who}</span>
        <span>{formatDateTime(message.timestamp)}</span>
      </div>
      <div className="space-y-1">
        {message.parts.map((p, i) => {
          if (p.type === 'text' && 'text' in p)
            return (
              <p key={i} className="whitespace-pre-wrap">
                {p.text}
              </p>
            );
          if (p.type === 'reasoning' && 'text' in p)
            return (
              <p key={i} className="whitespace-pre-wrap text-xs italic text-muted-foreground">
                {p.text}
              </p>
            );
          if (p.type === 'dynamic-tool' && 'toolName' in p)
            return (
              <p key={i} className="font-mono text-xs text-muted-foreground">
                → {p.toolName}{' '}
                {p.state === 'output-error' ? (
                  <span className="text-destructive">failed: {p.errorText}</span>
                ) : p.state === 'output-available' ? (
                  '✓'
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> running
                  </span>
                )}
              </p>
            );
          return null;
        })}
      </div>
    </div>
  );
}

function latestTerminal(messages: Message[]): Terminal | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const parts = messages[i].parts;
    for (let j = parts.length - 1; j >= 0; j--) {
      const p = parts[j];
      if (p.type === 'data-terminal' && 'data' in p && p.data) return p.data as Terminal;
    }
  }
  return null;
}
